
// /pages/CreateNewSubjectForum.js

import React, { useEffect, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import NavigationBar from "../components/Navigation";
import Footer from "../components/Footer";
import {
  collection,
  doc,
  increment,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../config/firebasedeconfig";
import { UserContext } from "../UserContext";
import { forumCategories } from "../components/categoriesForum"; // Import des catégories communes

import ReactQuill from "react-quill";
import { SketchPicker } from "react-color";
import "react-quill/dist/quill.snow.css";
import "../styles/components/CreateNewSubjectForum-page.scss";
import { useLanguage } from '../contexts/LanguageContext';
import { XP_WEIGHTS } from "../components/levelUtils";
import ActivityGainToast from "../components/ActivityGainToast";
import { getForumQuotaStatus } from "../utils/forumQuotaUtils";
import ForumQuotaStatus from "../components/ForumQuotaStatus";

const formatActivityGainPoints = (value) => {
  const number = Number(value) || 0;

  return `+${Number.isInteger(number)
    ? number
    : number.toFixed(2).replace(/\.?0+$/, "")
    }`;
};

const CreateNewSubjectForum = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { username, userId, userData } = useContext(UserContext);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, []);

  // États du formulaire
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [content, setContent] = useState("");
  const [error, setError] = useState("");
  const [activityGainToast, setActivityGainToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forumQuotaRefreshKey, setForumQuotaRefreshKey] = useState(0);

  // État du color picker
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [pickerColor, setPickerColor] = useState({
    r: 34,
    g: 25,
    b: 77,
    a: 1.0,
  });

  // Référence à ReactQuill
  const quillRef = useRef(null);

  // Configuration de la toolbar
  const modules = {
    toolbar: {
      container: "#myToolbar",
    },
  };

  // Formats autorisés
  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "code-block",
    "formula",
    "list",
    "bullet",
    "script",
    "indent",
    "link",
    "image",
    "video",
    "clean",
    "align",
    "color",
  ];

  const showPostCreatedActivityToast = () => {
    setActivityGainToast({
      id: `post-created-${Date.now()}`,
      points: formatActivityGainPoints(XP_WEIGHTS.posts),
      unitLabel: t("profilMember.activity"),
      label: t("activityToast.postCreated"),
      variant: "post",
    });
  };

  // Gestion de la sélection de couleur
  const handleColorChange = (colorResult) => {
    setPickerColor(colorResult.hex);
    const { r, g, b, a } = colorResult.rgb;
    const rgbaColor = `rgba(${r}, ${g}, ${b}, ${a})`;
    if (quillRef.current) {
      quillRef.current.getEditor().format("color", rgbaColor);
    }
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !category || !content || content === "<p><br></p>") {
      setError(t('createNewTopic.errorAllFields'));
      return;
    }

    if (!userId) {
      setError(t("forumQuota.errorUserNotFound"));
      return;
    }

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const quotaStatus = await getForumQuotaStatus({
        userId,
        subscription: userData?.subscription,
      });

      if (!quotaStatus.allowed) {
        if (quotaStatus.reason === "daily-limit") {
          setError(
            t("forumQuota.dailyLimit", {
              limit: quotaStatus.limits.daily,
            })
          );
        } else if (quotaStatus.reason === "rolling-30-days-limit") {
          setError(
            t("forumQuota.rolling30DaysLimit", {
              limit: quotaStatus.limits.rolling30Days,
            })
          );
        } else {
          setError(t("forumQuota.unavailable"));
        }

        return;
      }
      const postRef = doc(collection(db, "posts"));
      const usageRef = doc(
        collection(db, "users", userId, "forumUsage")
      );
      const userRef = doc(db, "users", userId);

      await runTransaction(db, async (transaction) => {
        transaction.set(postRef, {
          title,
          category,
          content,
          timestamp: serverTimestamp(),
          author: username || "Anonyme",
          authorId: userId,
          replies: 0,
          likes: [],
        });

        transaction.set(usageRef, {
          type: "post",
          postId: postRef.id,
          createdAt: serverTimestamp(),
        });

        transaction.update(userRef, {
          "xp.sources.posts": increment(1),
          "xp.updatedAt": serverTimestamp(),
        });
      });

      setForumQuotaRefreshKey((prev) => prev + 1);

      setTitle("");
      setCategory("");
      setContent("");
      setError("");


      showPostCreatedActivityToast();

      window.setTimeout(() => {
        navigate("/Forum");
      }, 1800);
    } catch (err) {
      console.error("Erreur lors de l'ajout du sujet :", err);
      setError(t('createNewTopic.errorAddingTopic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Utilisation des catégories depuis forumCategories
  const categories = forumCategories || [];
  const popularCategories = categories.slice(0, 5);
  const otherCategories = categories.slice(5);

  // Pour le menu secondaire (si besoin d'une liste fixe complémentaire)
  const secondaryCategories = [
    { id: "action", name: "Action" },
    { id: "aventure", name: "Aventure" },
    { id: "animation", name: "Animation" },
    { id: "biographie", name: "Biographie" },
    { id: "comedie", name: "Comédie" },
    { id: "crime", name: "Crime" },
    { id: "documentaire", name: "Documentaire" },
    { id: "drame", name: "Drame" },
    { id: "fantastique", name: "Fantastique" },
    { id: "guerre", name: "Guerre" },
    { id: "histoire", name: "Histoire" },
    { id: "horreur", name: "Horreur" },
    { id: "musique", name: "Musique" },
    { id: "mystere", name: "Mystère" },
    { id: "romance", name: "Romance" },
    { id: "science-fiction", name: "Science-Fiction" },
    { id: "telefilm", name: "Téléfilm" },
    { id: "thriller", name: "Thriller" },
    { id: "western", name: "Western" },
  ];

  // Composant dropdown personnalisé avec les styles identiques à Forum.js
  const CustomDropdown = ({ selectedValue, onChange }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showSecondaryDropdown, setShowSecondaryDropdown] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState(-1);

    const containerStyle = {
      position: "relative",
      width: "100%",
      userSelect: "none",
      border: "1px solid #ccc",
      borderRadius: "4px",
      padding: "0.75rem",
      backgroundColor: "#222",
      color: "#fff",
      cursor: "pointer",
    };

    const selectedStyle = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    };

    const arrowStyle = {
      marginLeft: "0.5rem",
      transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.3s",
    };

    const listStyle = {
      margin: 0,
      padding: 0,
      listStyle: "none",
      position: "absolute",
      left: 0,
      top: "calc(100% + 4px)",
      width: "100%",
      backgroundColor: "#200211",
      border: "1px solid #ccc",
      borderRadius: "4px",
      boxShadow: "0 0 10px rgba(0,0,0,0.3)",
      zIndex: 9999,
    };

    const itemStyle = (isHovered) => ({
      padding: "0.75rem",
      color: "#fff",
      backgroundColor: isHovered ? "#D90093" : "transparent",
      transition: "background-color 0.2s",
    });

    const handleSelect = (catId) => {
      onChange(catId);
      setDropdownOpen(false);
      setShowSecondaryDropdown(false);
    };

    // Rechercher la catégorie dans forumCategories et secondaryCategories
    const selectedCategoryObj =
      categories.find((cat) => cat.id === selectedValue) ||
      secondaryCategories.find((cat) => cat.id === selectedValue) ||
      null;

    return (
      <div
        className="forum-category-dropdown"
        style={containerStyle}
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        <div style={selectedStyle}>
          <span>
            {selectedCategoryObj
              ? t(`forum.forumCategories.${selectedCategoryObj.id}`)
              : t('createNewTopic.categoryPlaceholder')}
          </span>
          <span style={arrowStyle}>▼</span>
        </div>
        {dropdownOpen && (
          <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <ul
              className="forum-category-dropdown__list"
              style={listStyle}
            >
              {popularCategories.map((cat, i) => (
                <li
                  key={cat.id}
                  style={itemStyle(i === hoveredIndex)}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(-1)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(cat.id);
                  }}
                >
                  {t(`forum.forumCategories.${cat.id}`)}
                </li>
              ))}
              <li
                style={itemStyle(false)}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSecondaryDropdown(!showSecondaryDropdown);
                }}
              >
                {t('forum.moreCategories')}
              </li>
              {showSecondaryDropdown && (
                <ul
                  style={{
                    ...listStyle,
                    top: "0",
                    left: "100%",
                    width: "200px",
                  }}
                  className="forum-category-dropdown__secondary"
                >
                  {secondaryCategories.map((cat, i) => (
                    <li
                      key={cat.id}
                      style={itemStyle(i === hoveredIndex)}
                      onMouseEnter={() => setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex(-1)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(cat.id);
                      }}
                    >
                      {t(`forum.forumCategories.${cat.id}`)}
                    </li>
                  ))}
                </ul>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };


  return (
    <>
      <NavigationBar />
      <ActivityGainToast
        visible={Boolean(activityGainToast)}
        toastId={activityGainToast?.id}
        points={activityGainToast?.points}
        unitLabel={activityGainToast?.unitLabel}
        label={activityGainToast?.label}
        variant={activityGainToast?.variant}
        onDone={() => setActivityGainToast(null)}
      />
      <div
        className="CreateNewSubjectForum-page"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          backgroundBlendMode: "overlay",
          backgroundColor: "#200211",
          opacity: "0.9",
          overflowX: "visible",
          overflowY: "auto",
          minHeight: "100vh",
          position: "relative",
        }}
      >
        <div className="forum-newSubject-content">
          <h2
            style={{
              marginBottom: "1rem",
              color: "#D90093",
              textShadow: "1px 1px 2px #000000",
              textAlign: "center",
            }}
          >
            {t('createNewTopic.pageTitle')}
          </h2>
          {error && (
            <p className="error" style={{ color: "red", textAlign: "center" }}>
              {error}
            </p>
          )}

          <form onSubmit={handleSubmit}>
            <label style={{ display: "block", marginBottom: "1rem", color: "#FFFFFF" }}>
              {t('createNewTopic.title')}
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('createNewTopic.titlePlaceholder')}
                required
                style={{
                  marginTop: "0.5rem",
                  width: "100%",
                  padding: "0.8rem",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
              />
            </label>

            <label style={{ display: "block", marginBottom: "1rem", color: "#FFFFFF" }}>
              {t('createNewTopic.category')}
              <div style={{ marginTop: "0.5rem" }}>
                <CustomDropdown
                  selectedValue={category}
                  onChange={(newCat) => setCategory(newCat)}
                />
              </div>
            </label>

            <div id="myToolbar" style={{ marginBottom: "1rem" }}>
              <span className="ql-formats">
                <select className="ql-header" defaultValue="">
                  <option value="1" />
                  <option value="2" />
                  <option value="">Normal</option>
                </select>
              </span>
              <span className="ql-formats">
                <button className="ql-bold" />
                <button className="ql-italic" />
                <button className="ql-underline" />
                <button className="ql-strike" />
                <button className="ql-blockquote" />
                <button className="ql-code-block" />
                <button className="ql-formula" />
              </span>
              <span className="ql-formats">
                <button className="ql-list" value="ordered" />
                <button className="ql-list" value="bullet" />
                <button className="ql-indent" value="-1" />
                <button className="ql-indent" value="+1" />
              </span>
              <span className="ql-formats">
                <select className="ql-align" />
              </span>
              <span className="ql-formats">
                <button className="ql-link" />
                <button className="ql-image" />
                <button className="ql-video" />
              </span>
              <span className="ql-formats">
                <button className="ql-clean" />
              </span>
              <span className="ql-formats">
                <button
                  type="button"
                  onClick={() => setShowColorPicker((prev) => !prev)}
                  className="ql-Chromecolor"
                  style={{
                    marginLeft: "1rem",
                    backgroundColor: "#000",
                    backgroundImage: "url('/images/chromeWheelButton.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                ></button>
              </span>
            </div>

            {showColorPicker && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <SketchPicker
                  color={pickerColor}
                  onChange={(colorResult) => {
                    setPickerColor(colorResult.rgb);
                    const rgbaColor = `rgba(${colorResult.rgb.r}, ${colorResult.rgb.g}, ${colorResult.rgb.b}, ${colorResult.rgb.a})`;
                    if (quillRef.current) {
                      quillRef.current.getEditor().format("color", rgbaColor);
                    }
                  }}
                  onChangeComplete={(finalColor) => {
                    console.log("Couleur finale :", finalColor.hex);
                  }}
                  disableAlpha={false}
                  presetColors={[
                    "#D90093",
                    "#FF33CC",
                    "#200211",
                    "#00FFFF",
                    "#FFFFFF",
                    "#000000",
                    "#507256",
                    "#F44336",
                    "#E91E63",
                    "#9C27B0",
                    "#673AB7",
                    "#3F51B5",
                    "#2196F3",
                    "#03A9F4",
                    "#00BCD4",
                    "#009688",
                    "#4CAF50",
                    "#8BC34A",
                    "#CDDC39",
                    "#FFEB3B",
                    "#FFC107",
                    "#FF9800",
                    "#FF5722",
                    "#795548",
                    "#9E9E9E",
                    "#607D8B",
                  ]}
                />
              </div>
            )}

            <label style={{ display: "block", marginBottom: "1rem", color: "#FFFFFF" }}>
              {t('createNewTopic.content')}

              <ReactQuill
                ref={quillRef}
                value={content}
                onChange={setContent}
                modules={modules}
                formats={formats}
                theme="snow"
                placeholder={t('createNewTopic.contentPlaceholder')}
                style={{ marginTop: "0.5rem", zIndex: "99998" }}
              />
            </label>

            <ForumQuotaStatus
              userId={userId}
              subscription={userData?.subscription}
              refreshKey={forumQuotaRefreshKey}
            />

            <div
              className="create-subject-actions"
              style={{
                display: "flex",
                gap: "1rem",
                marginTop: "1rem"
              }}
            >
              <button
                type="submit"
                disabled={isSubmitting}
                className="buttonForumModal"
                style={{
                  backgroundColor: "#D90093",
                  flex: 1,
                  padding: "0.8rem",
                  border: "none",
                  borderRadius: "4px",
                  color: "#fff",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {t('createNewTopic.publish')}
              </button>
              <button
                type="button"
                onClick={() => navigate("/Forum")}
                className="buttonForumModal"
                style={{
                  backgroundColor: "#D90093",
                  flex: 1,
                  padding: "0.8rem",
                  border: "none",
                  borderRadius: "4px",
                  color: "#fff",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {t('createNewTopic.cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CreateNewSubjectForum;





